import React from "react";
import _ from "lodash";
import { connect } from "react-redux";
import * as Excel from "exceljs/dist/es5/exceljs.browser";
import { FormattedMessage, injectIntl } from "react-intl";

import {
  SubdirectoryArrowRight,
  Reorder,
  Description,
  Info,
} from "components/Icons";
import ToggleSwitch from "components/ToggleSwitch";
import Select from "components/Select";
import { getFormatted } from "helpers/formatting";
import { downloadFile } from "helpers/download";
import { getTranslation } from "helpers/common";
import Swal from "helpers/swal";

import "./style.scss";

interface IProps {
  options?: any[];
  switchUnread?: boolean;
  handleFieldChooser: (any) => void;
  handleSelect: (any) => void;
  toggleUnread?: (any) => void;
  excelColumns?: any[];
  selectedOption?: string;
  getExcelData?: () => Promise<any[]>;
  entityName?: string;
  downloadExcel?: (any) => void;
  intl: any;
  isRegularUser: boolean;
}

interface IState {
  isExcelLoading: boolean;
}

class TopButtons extends React.Component<IProps, IState> {
  constructor(props) {
    super(props);

    this.state = {
      isExcelLoading: false,
    };
  }

  public getDefaultOptions = () =>
    this.props.isRegularUser
      ? [
          { value: "gelezen", label: getTranslation("MARK_AS_READ") },
          { value: "ongelezen", label: getTranslation("MARK_AS_UNREAD") },
          { value: "pdf", label: getTranslation("OPEN_PDF") },
        ]
      : [{ value: "pdf", label: getTranslation("OPEN_PDF") }];

  public handleDownload(excelColumns, excelData, name) {
    const {
      intl: { formatMessage },
    } = this.props;
    console.log("IN DOWNLOAD");
    const formattedName = name ? formatMessage({ id: name }) : "CNBExcel";
    const workbook = new Excel.Workbook();
    const worksheet = workbook.addWorksheet(formattedName);
    const borderStyles = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
    console.log("BEFORE APPLY STYLES");

    // Applies styles to columns
    worksheet.columns = _.map(excelColumns, (c) => {
      return {
        header: formatMessage({ id: c.title }, c.customData),
        key: c.key,
        width: c.width || 20,
      };
    });
    worksheet.getRow(1).font = { bold: true };

    let tempArr;

    console.log("AFTER APPLY STYLES");

    // Makes object flatten (all children to one level)
    let data = _.flatten(
      _.map(excelData, (row) => {
        tempArr = [];
        _.map(excelColumns, (col) => {
          if (col.isArray) {
            const tempKey = col.key.split("[")[0];
            _.map(_.get(row, tempKey), (__, lineInd) => {
              if (!tempArr[lineInd]) {
                tempArr.push(_.cloneDeep(row));
                tempArr[lineInd].index = lineInd;
              }
              tempArr[lineInd][col.key] = col.render
                ? col.render(row, col, lineInd)
                : _.get(row, _.replace(col.key, "index", lineInd));
            });
          }
        });
        return tempArr;
      })
    );
    console.log("AFTER FLATTEN");

    // Fills cells of table
    data = _.map(data.length > 0 ? data : excelData, (item) => {
      return _.zipObject(
        _.map(excelColumns, "key"),
        _.map(excelColumns, (v) => {
          return v.render && !v.isArray
            ? v.render(item, v)
            : v.type === "currency" ||
              v.type === "number" ||
              v.type === "absolue" ||
              v.type === "percent"
            ? _.get(item, v.key)
            : getFormatted(_.get(item, v.key), v.type);
        })
      );
    });

    worksheet.addRows(data);

    // Adds format to number fields
    let tempCell;
    _.each(excelColumns, (c, index) => {
      _.each(_.range(2, worksheet.rowCount + 1), (r) => {
        tempCell = worksheet.getCell(worksheet.getColumn(index + 1).letter + r);
        if (c.type === "currency") {
          tempCell.numFmt = "€ #,##0.00;€ -#,##0.00;0";
        } else if (c.type === "number") {
          tempCell.numFmt = "#,##0.00;-#,##0.00;0";
        } else if (c.type === "absolute") {
          tempCell.numFmt = "#,##0.00;#,##0.00;0";
        } else if (c.type === "percent") {
          tempCell.numFmt = "General %";
        }
      });
    });

    // Adds border to table
    worksheet.eachRow({ includeEmpty: true }, (row) => {
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = borderStyles;
      });
    });

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([new Uint8Array(buffer)], {
        type: "application/xlsx",
      });
      downloadFile(blob, `${formattedName}.xlsx`);
    });
  }

  public handleExcelClick = () => {
    const {
      getExcelData,
      downloadExcel,
      excelColumns,
      entityName = "",
      intl: { formatMessage },
    } = this.props;

    if (getExcelData) {
      this.handleLoader();
      getExcelData()
        .then((excelData) => {
          this.handleLoader();
          if (excelData && excelData.length > 0) {
            downloadExcel
              ? downloadExcel(excelData)
              : this.handleDownload(excelColumns, excelData, entityName);
          } else {
            Swal.alert("Info", formatMessage({ id: "NO_DATA_EXPORT" }));
          }
        })
        .catch((err) => {
          this.handleLoader();
          console.error(err);
        });
    }
  };

  private handleLoader = () => {
    this.setState({ isExcelLoading: !this.state.isExcelLoading });
  };

  public render() {
    const {
      options = this.getDefaultOptions(),
      switchUnread = false,
      handleFieldChooser,
      handleSelect,
      toggleUnread,
      selectedOption = "",
      getExcelData,
      entityName = "",
    } = this.props;
    const { isExcelLoading } = this.state;

    return (
      <div id="top-buttons">
        <div className="dropdown-button">
          <label>
            <SubdirectoryArrowRight />
            <FormattedMessage id="SELECTED" />
          </label>
          <Select
            id={`select-${_.lowerCase(entityName)}-action`}
            onChange={handleSelect}
            options={options}
            selectedOption={selectedOption}
          />
          {getExcelData && (
            <div
              id={`download-${entityName}-excel`}
              className="download-excel"
              title="Download excel"
            >
              {isExcelLoading ? (
                <img src="/assets/images/loading.gif" />
              ) : (
                <>
                  <Description
                    className="download-icon"
                    onClick={this.handleExcelClick}
                  />
                  <Info className="info-icon" />
                </>
              )}
              <div className="info-message">
                <FormattedMessage id="EXCEL_INFO" />
              </div>
            </div>
          )}
        </div>
        <div className="top-buttons-right">
          <div className="open-field-chooser" onClick={handleFieldChooser}>
            <FormattedMessage id="CHOOSE_COLUMN" tagName="label" />
            <Reorder />
          </div>
          {toggleUnread && (
            <ToggleSwitch
              label="SHOW_UNREAD"
              on={switchUnread}
              onClick={toggleUnread}
            />
          )}
        </div>
      </div>
    );
  }
}

export default connect((state) => ({
  isRegularUser:
    _.get(state.oidc, "user.profile.CNBMedewerker", "false") === "false",
}))(injectIntl(TopButtons));
